import { createLDSAuthApi } from '@laserpecker/lds-auth-api'

const GIS_SCRIPT_ID = 'lds-auth-google-identity-services'
let gisPromise

export function beginLifecycle(state) {
  state.connected = true
  state.lifecycleGeneration = (state.lifecycleGeneration ?? 0) + 1
  return state.lifecycleGeneration
}

export function endLifecycle(state) {
  state.connected = false
  state.lifecycleGeneration = (state.lifecycleGeneration ?? 0) + 1
}

export function lifecycleIsActive(state, generation) {
  return state.connected === true && state.lifecycleGeneration === generation
}

export function beginExchange(state) {
  state.abortController?.abort()
  const exchange = {
    controller: new AbortController(),
    generation: (state.exchangeGeneration ?? 0) + 1,
  }
  state.exchangeGeneration = exchange.generation
  state.abortController = exchange.controller
  return exchange
}

export function exchangeIsActive(state, lifecycleGeneration, exchange) {
  return (
    exchange !== undefined &&
    lifecycleIsActive(state, lifecycleGeneration) &&
    state.exchangeGeneration === exchange.generation &&
    state.abortController === exchange.controller &&
    exchange.controller.signal.aborted === false
  )
}

export function stopLifecycle(state) {
  if (state.connected) endLifecycle(state)
  clearTimeout(state.promptTimer)
  state.promptTimer = undefined
  state.abortController?.abort()
  state.abortController = undefined
}

export function loadGIS() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google)
  if (gisPromise) return gisPromise
  gisPromise = new Promise((resolve, reject) => {
    let script = document.getElementById(GIS_SCRIPT_ID)
    if (!script) {
      script = document.createElement('script')
      script.id = GIS_SCRIPT_ID
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      document.head.append(script)
    }
    script.addEventListener(
      'load',
      () => {
        if (window.google?.accounts?.id) {
          resolve(window.google)
          return
        }
        script.remove()
        gisPromise = undefined
        reject(new Error('gis_unavailable'))
      },
      { once: true },
    )
    script.addEventListener(
      'error',
      () => {
        script.remove()
        gisPromise = undefined
        reject(new Error('gis_unavailable'))
      },
      { once: true },
    )
  })
  return gisPromise
}

if (typeof window !== 'undefined' && typeof HTMLElement !== 'undefined') {
  class LDSAuthGoogleOneTap extends HTMLElement {
    connectedCallback() {
      if (this.connected) return
      this.removeAttribute('data-failed')
      const generation = beginLifecycle(this)
      const { clientId, apiBaseUrl } = this.dataset
      if (!clientId || !apiBaseUrl) {
        endLifecycle(this)
        return
      }
      try {
        this.api = createLDSAuthApi({
          apiBaseUrl,
          primaryOrigin: this.dataset.primaryOrigin,
          shopifyOrigin: this.dataset.shopifyOrigin,
          accountPath: this.dataset.accountPath,
          siteId: Number(this.dataset.siteId),
        })
      } catch {
        endLifecycle(this)
        return
      }
      this.start(generation)
    }

    disconnectedCallback() {
      if (!this.connected && !this.abortController && !this.promptTimer) return
      this.stop()
      this.hidden = true
    }

    stop() {
      stopLifecycle(this)
      this.api = undefined
      try {
        window.google?.accounts?.id?.cancel?.()
      } catch {}
    }

    async start(generation) {
      try {
        const google = await loadGIS()
        if (!lifecycleIsActive(this, generation)) return
        google.accounts.id.initialize({
          client_id: this.dataset.clientId,
          callback: ({ credential }) => {
            if (lifecycleIsActive(this, generation)) this.exchange(credential, generation)
          },
          context: 'signin',
          cancel_on_tap_outside: true,
          itp_support: true,
        })
        if (!lifecycleIsActive(this, generation)) return
        this.promptTimer = setTimeout(() => {
          this.promptTimer = undefined
          if (!lifecycleIsActive(this, generation)) return
          google.accounts.id.prompt()
        }, 500)
      } catch {
        this.fail(generation)
      }
    }

    async exchange(credential, generation) {
      if (!lifecycleIsActive(this, generation)) return
      if (typeof credential !== 'string' || credential.length === 0) return this.fail(generation)
      const exchange = beginExchange(this)
      try {
        const result = await this.api.googleOneTapExchange(
          { credential },
          { signal: exchange.controller.signal },
        )
        if (!exchangeIsActive(this, generation, exchange)) return
        window.location.assign(result.redirectUrl)
      } catch {
        if (exchangeIsActive(this, generation, exchange)) this.fail(generation)
      } finally {
        if (
          this.exchangeGeneration === exchange.generation &&
          this.abortController === exchange.controller
        ) {
          this.abortController = undefined
        }
      }
    }

    fail(generation) {
      if (!lifecycleIsActive(this, generation)) return
      this.setAttribute('data-failed', '')
      this.stop()
      this.hidden = true
    }
  }

  if (!customElements.get('lds-auth-google-one-tap')) {
    customElements.define('lds-auth-google-one-tap', LDSAuthGoogleOneTap)
  }
  document.addEventListener('shopify:section:load', (event) => {
    event.target
      ?.querySelectorAll?.('lds-auth-google-one-tap')
      .forEach((element) => element.connectedCallback())
  })
}
